import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { RiArrowDownWideLine, RiArrowUpWideLine } from "react-icons/ri";
import styled from "styled-components";
import { padNumberWithZero } from "../../helpers/utils";

interface ITimePickViewProps {
  hour: number;
  minute: number;
  onHourChange: (hour: string) => any;
  onMinuteChange: (minute: string) => any;
  disableHour?: number | null;
  disableMinute?: number | null;
  pickType: "start" | "end";
}

const TimePickView = ({
  hour,
  minute,
  onHourChange,
  onMinuteChange,
  disableHour,
  disableMinute,
  pickType,
}: ITimePickViewProps) => {
  const [hourValue, setHourValue] = useState(padNumberWithZero(hour));
  const [minuteValue, setMinuteValue] = useState(padNumberWithZero(minute));
  const [isIncreaseHourDisabled, setIsIncreaseHourDisabled] = useState(false);
  const [isDecreaseHourDisabled, setIsDecreaseHourDisabled] = useState(false);
  const [isMinuteButtonsDisabled, setIsMinuteButtonsDisabled] = useState(false);
  const hourInputRef = useRef<HTMLInputElement | null>(null);
  const minuteInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setHourValue(padNumberWithZero(hour));
    setMinuteValue(padNumberWithZero(minute));
    updateButtonStates(hour, minute);
  }, [hour, minute, disableHour, disableMinute]);

  // Function to update button states based on current hour and minute
  const updateButtonStates = (currentHour: number, currentMinute: number) => {
    if (!hasDisabledTime) {
      setIsIncreaseHourDisabled(false);
      setIsDecreaseHourDisabled(false);
      setIsMinuteButtonsDisabled(false);
      return;
    }

    if (pickType === "start") {
      // For start time
      // Disable both increase and decrease if disabled hour is 0
      if (disableHour === 0) {
        setIsIncreaseHourDisabled(true);
        setIsDecreaseHourDisabled(true);
        setIsMinuteButtonsDisabled(true);
        return;
      }

      // Disable increase if:
      // 1. Current hour >= disabled hour, OR
      // 2. Current hour + 1 === disabled hour AND disabled minute === 0
      setIsIncreaseHourDisabled(
        currentHour >= disableHour ||
        (currentHour + 1 === disableHour && disableMinute === 0)
      );
      
      // Disable decrease if we would reach or exceed disabled hour
      setIsDecreaseHourDisabled(currentHour - 1 >= disableHour);

      // Disable minute buttons when at disabled hour
      setIsMinuteButtonsDisabled(currentHour === disableHour);
    } else {
      // For end time
      // Never disable increase button for end time
      setIsIncreaseHourDisabled(false);
      
      // Disable decrease if:
      // 1. Current hour - 1 < disabled hour, OR
      // 2. Current hour === disabled hour + 1 AND disabled minute === 30
      setIsDecreaseHourDisabled(
        currentHour - 1 < disableHour ||
        (currentHour === disableHour + 1 && disableMinute === 30)
      );

      // Disable minute buttons when at disabled hour
      setIsMinuteButtonsDisabled(currentHour === disableHour);
    }
  };

  const hasDisabledTime = disableHour !== null && disableHour !== undefined && 
                         disableMinute !== null && disableMinute !== undefined;

  const getValidMinute = (currentHour: number, currentMinute: number) => {
    if (!hasDisabledTime || currentHour !== disableHour) return currentMinute;
    
    if (pickType === "start") {
      // For start time, always keep it at least 30 minutes before end time
      return disableMinute === 0 ? 30 : 0;
    }
    if (pickType === "end") {
      if (currentMinute <= disableMinute) {
        return disableMinute === 30 ? 0 : 30;
      }
    }
    return currentMinute;
  };

  const minuteControl = {
    onMinuteChange(e: React.ChangeEvent<HTMLInputElement>) {
      let value = e.target.value;
      if (value.length > 2) {
        value = value.slice(-2);
      }
      if (value.length <= 2 && ["0", "00", "30"].includes(value)) {
        const numValue = parseInt(value) || 0;
        const currentHour = parseInt(hourValue) || 0;
        
        if (hasDisabledTime && currentHour === disableHour) {
          const numDisableMinute = parseInt(disableMinute.toString());
          if (pickType === "end") {
            if (numValue <= numDisableMinute) {
              const newMinute = numDisableMinute === 30 ? "00" : "30";
              setMinuteValue(newMinute);
              onMinuteChange(newMinute);
              return;
            }
          } else if (pickType === "start") {
            // For start time, always keep it at least 30 minutes before end time
            const newMinute = numDisableMinute === 0 ? "30" : "00";
            setMinuteValue(newMinute);
            onMinuteChange(newMinute);
            return;
          }
        }
        
        const validMinute = getValidMinute(currentHour, numValue);
        const paddedValue = padNumberWithZero(validMinute);
        setMinuteValue(paddedValue);
        onMinuteChange(paddedValue);
      }
    },
    updateMinute(ref: React.RefObject<HTMLInputElement>) {
      const currentValue = ref.current?.value;
      if (!currentValue) return;

      const currentHour = parseInt(hourValue);
      if (hasDisabledTime && currentHour === disableHour) {
        if (pickType === "start") {
          // For start time, prevent any minute changes when at disabled hour
          return;
        } else if (pickType === "end") {
          const nextValue = currentValue === "00" ? "30" : "00";
          const numNextValue = parseInt(nextValue);
          const numDisableMinute = parseInt(disableMinute.toString());
          if (numNextValue <= numDisableMinute) return;
        }
      }

      const newValue = currentValue === "00" ? "30" : "00";
      setMinuteValue(newValue);
      onMinuteChange(newValue);
    },
  };

  const hourControl = {
    onHourChange(e: React.ChangeEvent<HTMLInputElement>) {
      const value = e.target.value;
      if (/^\d{0,2}$/.test(value)) {
        if (value.length === 2) {
          const numValue = parseInt(value, 10);
          if (numValue >= 0 && numValue <= 23) {
            if (hasDisabledTime) {
              if (pickType === "start") {
                if (numValue > disableHour) {
                  const validHour = padNumberWithZero(disableHour);
                  setHourValue(validHour);
                  onHourChange(validHour);
                  return;
                }
                if (numValue === disableHour) {
                  // For start time, always keep it at least 30 minutes before end time
                  const numDisableMinute = parseInt(disableMinute.toString());
                  const newMinute = numDisableMinute === 0 ? "30" : "00";
                  setMinuteValue(newMinute);
                  onMinuteChange(newMinute);
                }
              }
              if (pickType === "end") {
                if (numValue < disableHour) {
                  const validHour = padNumberWithZero(disableHour);
                  setHourValue(validHour);
                  onHourChange(validHour);
                  return;
                }
                if (numValue === disableHour) {
                  const currentMinute = parseInt(minuteValue);
                  const numDisableMinute = parseInt(disableMinute.toString());
                  if (currentMinute <= numDisableMinute) {
                    const newMinute = numDisableMinute === 30 ? "00" : "30";
                    setMinuteValue(newMinute);
                    onMinuteChange(newMinute);
                  }
                }
              }
            }
            const paddedValue = value.padStart(2, "0");
            setHourValue(paddedValue);
            onHourChange(paddedValue);
          }
        } else {
          setHourValue(value);
        }
      }
    },
    increaseHour(ref: React.RefObject<HTMLInputElement>) {
      const value = ref.current?.value;
      if (!value) {
        setHourValue("00");
        return;
      }

      let numValue = parseInt(value);
      if (numValue >= 0 && numValue <= 23) {
        if (hasDisabledTime) {
          // START TIME LOGIC
          // If pickType is "start", we need to ensure we don't exceed the disabled hour
          // and handle special cases for minutes
          if (pickType === "start") {
            // Prevent increasing if we would go beyond the disabled hour
            if (numValue + 1 > disableHour) return;
            // If we would reach the disabled hour, only allow it if minutes are 30
            // This prevents setting start time to same hour as end time when end time minutes are 00
            if (numValue + 1 === disableHour && disableMinute === 0) return;
          }

          // END TIME LOGIC
          // If pickType is "end", we need to ensure we don't conflict with start time
          // When start time minutes are 00, prevent end time from being same hour
          if (pickType === "end" && numValue + 1 === disableHour && disableMinute === 0) return;
          
          // Calculate the next hour value, wrapping around to 00 if we're at 23
          const newValue = numValue === 23 ? "00" : padNumberWithZero(numValue + 1);
          
          // SPECIAL CASE: End time minutes adjustment
          // When end time reaches the disabled hour and start time minutes are 30,
          // we need to ensure end time minutes are at least 30 minutes ahead
          if (pickType === "end" && numValue + 1 === disableHour && disableMinute === 30) {
            const currentMinute = parseInt(minuteValue);
            const numDisableMinute = parseInt(disableMinute.toString());
            if (currentMinute <= numDisableMinute) {
              // If current minutes are less than or equal to disabled minutes,
              // adjust to next valid minute value
              const newMinute = numDisableMinute === 30 ? "00" : "30";
              setMinuteValue(newMinute);
              onMinuteChange(newMinute);
            }
          } else {
            // For all other cases, set minutes to 00 when increasing hour
            setMinuteValue("00");
            onMinuteChange("00");
          }
          
          setHourValue(newValue);
          onHourChange(newValue);
        } else {
          // If no disabled time, simply increment the hour and set minutes to 00
          const newValue = numValue === 23 ? "00" : padNumberWithZero(numValue + 1);
          setHourValue(newValue);
          onHourChange(newValue);
          setMinuteValue("00");
          onMinuteChange("00");
        }
      }
    },
    decreaseHour(ref: React.RefObject<HTMLInputElement>) {
      const value = ref.current?.value;
      if (!value) {
        setHourValue("00");
        return;
      }

      let numValue = parseInt(value);
      if (numValue >= 0 && numValue <= 23) {
        if (hasDisabledTime) {
          // START TIME LOGIC
          // If pickType is "start", prevent decreasing if we would reach or exceed the disabled hour
          if (pickType === "start" && numValue - 1 >= disableHour) return;

          // END TIME LOGIC
          // If pickType is "end", we need to ensure we don't go below start time
          // and handle special cases for minutes
          if (pickType === "end") {
            // Prevent decreasing if we would go below the disabled hour
            if (numValue - 1 < disableHour) return;
            // If we would reach the disabled hour, only allow it if minutes are 00
            // This prevents setting end time to same hour as start time when start time minutes are 30
            if (numValue - 1 === disableHour && disableMinute === 30) return;
          }
          
          // Calculate the previous hour value, wrapping around to 23 if we're at 00
          const newValue = numValue === 0 ? "23" : padNumberWithZero(numValue - 1);
          
          // SPECIAL CASE: End time minutes adjustment
          // When end time reaches the disabled hour, we need to ensure minutes are valid
          if (pickType === "end" && numValue - 1 === disableHour) {
            const currentMinute = parseInt(minuteValue);
            const numDisableMinute = parseInt(disableMinute.toString());
            if (currentMinute <= numDisableMinute) {
              // If current minutes are less than or equal to disabled minutes,
              // adjust to next valid minute value
              const newMinute = numDisableMinute === 30 ? "00" : "30";
              setMinuteValue(newMinute);
              onMinuteChange(newMinute);
            }
          }
          
          setHourValue(newValue);
          onHourChange(newValue);
        } else {
          // If no disabled time, simply decrement the hour
          const newValue = numValue === 0 ? "23" : padNumberWithZero(numValue - 1);
          setHourValue(newValue);
          onHourChange(newValue);
        }
      }
    },
  };

  useEffect(() => {
    if (!hasDisabledTime) return;
    //case is user selected date but has invalid time range
    //this is to handle the case where both buttons are disabled and we need to set the hour to 1 hour more or less than the disabled hour
    //this is to prevent the hour from being stuck at the disabled hour when both buttons are disabled
    if (isIncreaseHourDisabled && isDecreaseHourDisabled) {
      if (pickType === "start") {
        const newHour = disableHour < 1 ? 0 : disableHour - 1;
        const paddedHour = padNumberWithZero(newHour);
        setHourValue(paddedHour);
        onHourChange(paddedHour);
      } else {
        const newHour = disableHour + 1;
        const paddedHour = padNumberWithZero(newHour);
        setHourValue(paddedHour);
        onHourChange(paddedHour);
      }
    }
  }, [isIncreaseHourDisabled, isDecreaseHourDisabled, disableHour, hasDisabledTime, pickType]);

  return (
    <Wrapper>
      <h4 className="heading">Select a time</h4>
      <div className="time-select-control">
        <div className="select-control-container hour-select">
          <button
            className="btn control increase-control"
            onClick={() => hourControl.increaseHour(hourInputRef)}
            disabled={isIncreaseHourDisabled}
          >
            <RiArrowUpWideLine className="control-icon" />
          </button>
          <NoSpinnerInput
            aria-label="Hour Input"
            maxLength={2}
            type="number"
            name="hour"
            ref={hourInputRef}
            value={hourValue}
            onChange={hourControl.onHourChange}
            className="time-input"
          />
          <button
            className="btn control decrease-control"
            onClick={() => hourControl.decreaseHour(hourInputRef)}
            disabled={isDecreaseHourDisabled}
          >
            <RiArrowDownWideLine className="control-icon" />
          </button>
        </div>
        <div className="colon">:</div>
        <div className="select-control-container minute-select">
          <button
            className="btn control increase-control"
            onClick={() => minuteControl.updateMinute(minuteInputRef)}
            disabled={isMinuteButtonsDisabled}
          >
            <RiArrowUpWideLine className="control-icon" />
          </button>
          <NoSpinnerInput
            aria-label="Minute Input"
            maxLength={2}
            type="number"
            name="minute"
            ref={minuteInputRef}
            value={minuteValue}
            onChange={minuteControl.onMinuteChange}
            className="time-input"
          />
          <button
            className="btn control decrease-control"
            onClick={() => minuteControl.updateMinute(minuteInputRef)}
            disabled={isMinuteButtonsDisabled}
          >
            <RiArrowDownWideLine className="control-icon" />
          </button>
        </div>
      </div>
    </Wrapper>
  );
};

export default TimePickView;

const NoSpinnerInput = styled(Form.Control)`
  -moz-appearance: textfield;
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  color: ${({ theme }) => theme.text};
  background-color: ${({ theme }) => theme.date_range_picker.custom.background};
`;

const Wrapper = styled.div`
  .heading {
    text-align: center !important;
  }
  height: 256px;
  width: 268px;
  padding: 30px 20px 40px;
  .selected-date-header {
    display: flex;
    justify-content: space-between;
    .selected-date {
      cursor: pointer;
      font-size: 20px;
      font-weight: 700;
      line-height: 26px;
      user-select: none;
    }
    .nav-btn {
      cursor: pointer;
      user-select: none;
    }
  }
  .time-select-control {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    padding: 20px;
    .colon {
      text-align: center;
      padding: 30px 0px;
      font-size: 26px;
      font-weight: bolder;
    }
    .time-input {
      background-color: ${({ theme }) => theme.date_range_picker.custom.background};
    }
    .select-control-container {
      .form-control {
        margin-top: 2px;
        width: 46px;
      }
      .control {
        background: #99beeb;
        height: 26px;
        &:disabled {
          background: #cccccc;
          cursor: not-allowed;
          opacity: 0.7;
        }
        .control-icon {
          font-size: 20px;
          font-weight: 900;
          color: rgb(76, 100, 129);
          margin-bottom: 10px;
        }
      }
    }
  }
`;
